import type { Patch, OscillatorType, FilterType, LfoTarget, LfoShape } from "../engine/types";
import { attachParamDrag } from "./paramDrag";

type NumFmt = (v: number) => string;

const hz: NumFmt = (v) => (v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${Math.round(v)}`);
const sec: NumFmt = (v) => `${v.toFixed(2)}s`;
const cents: NumFmt = (v) => `${Math.round(v)}`;
const ratio: NumFmt = (v) => v.toFixed(2);

// A selectable parameter, handed to the right-edge context slider when its
// label is tapped. The context slider reads/writes through get/set.
export interface ParamRef {
  label: string;
  min: number;
  max: number;
  step: number;
  scale: "lin" | "log";
  fmt: NumFmt;
  get: () => number;
  set: (v: number) => void;
}

interface FaderOpts {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  fmt: NumFmt;
  scale?: "lin" | "log";
}

function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

// Compact waveform glyphs (also used for LFO shapes).
function waveIcon(kind: string): string {
  const paths: Record<string, string> = {
    sine: "M1 7 Q6.5 0 12 7 T23 7",
    square: "M1 12 V3 H8 V12 H15 V3 H23",
    sawtooth: "M1 12 L12 2 L12 12 L23 2",
    triangle: "M1 12 L7 2 L13 12 L19 2 L23 7",
    pulse: "M1 12 V3 H5 V12 H15 V3 H19 V12 H23",
    noise: "M1 7 L3 3 L4 11 L6 4 L8 10 L10 2 L12 11 L14 4 L16 9 L18 3 L20 11 L22 6 L23 8",
  };
  return `<svg viewBox="0 0 24 14" width="22" height="13" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"><path d="${paths[kind] ?? paths.sine}"/></svg>`;
}

function stage(container: HTMLElement, title: string, modifier?: string): HTMLElement {
  const s = el("section", "stage" + (modifier ? ` ${modifier}` : ""));
  const h = el("h3");
  h.textContent = title;
  s.append(h);
  container.append(s);
  return s;
}

function faderRow(parent: HTMLElement): HTMLElement {
  const f = el("div", "faders");
  parent.append(f);
  return f;
}

// A vertical fader: drag the track to change; tap the name/value to hand it to
// the right-edge context slider (which then mirrors it for fine control).
function vfader(
  root: HTMLElement,
  parent: HTMLElement,
  o: FaderOpts,
  onInput: (v: number) => void,
  onSelect?: (ref: ParamRef) => void,
): void {
  const scale = o.scale ?? "lin";
  const col = el("div", "fader");
  const val = el("div", "fader-val");
  const track = el("div", "fader-track");
  const fill = el("div", "fader-fill");
  const thumb = el("div", "fader-thumb");
  track.append(fill, thumb);
  const name = el("div", "fader-name");
  name.textContent = o.label;
  col.append(val, track, name);
  parent.append(col);

  const toFrac = (v: number): number =>
    scale === "log" ? Math.log(v / o.min) / Math.log(o.max / o.min) : (v - o.min) / (o.max - o.min);
  const toVal = (f: number): number => {
    const raw = scale === "log" ? o.min * Math.pow(o.max / o.min, f) : o.min + f * (o.max - o.min);
    const stepped = Math.round(raw / o.step) * o.step;
    return Math.min(o.max, Math.max(o.min, stepped));
  };

  let cur = o.value;
  const draw = (): void => {
    const f = Math.min(1, Math.max(0, toFrac(cur)));
    fill.style.height = `${f * 100}%`;
    thumb.style.top = `${(1 - f) * 100}%`;
    val.textContent = o.fmt(cur);
  };
  const apply = (v: number): void => {
    cur = v;
    draw();
    onInput(v);
  };
  draw();

  let dragging = false;
  const setFromY = (clientY: number): void => {
    const r = track.getBoundingClientRect();
    const f = 1 - Math.min(1, Math.max(0, (clientY - r.top) / r.height));
    apply(toVal(f));
  };
  track.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    track.setPointerCapture(e.pointerId);
    dragging = true;
    setFromY(e.clientY);
  });
  track.addEventListener("pointermove", (e) => {
    if (dragging) setFromY(e.clientY);
  });
  const up = (): void => {
    dragging = false;
  };
  track.addEventListener("pointerup", up);
  track.addEventListener("pointercancel", up);

  const ref: ParamRef = {
    label: o.label,
    min: o.min,
    max: o.max,
    step: o.step,
    scale,
    fmt: o.fmt,
    get: () => cur,
    set: (v) => {
      cur = Math.min(o.max, Math.max(o.min, v));
      draw();
      onInput(cur);
    },
  };
  const selectThis = (): void => {
    if (!onSelect) return;
    root.querySelectorAll(".fader.selected").forEach((e) => e.classList.remove("selected"));
    col.classList.add("selected");
    onSelect(ref);
  };
  // The label chip is a control itself: drag up/down = adjust (with the HUD
  // readout — the big mobile-friendly target), plain tap = fine-tune on the
  // right context slider.
  attachParamDrag(name, () => ref, selectThis);
  val.addEventListener("click", selectThis);
}

interface RadioOption {
  value: string;
  html: string;
  title: string;
}

function iconRadio(
  parent: HTMLElement,
  label: string,
  options: readonly RadioOption[],
  current: string,
  onChange: (v: string) => void,
): void {
  const wrap = el("div", "radio");
  const name = el("div", "radio-label");
  name.textContent = label;
  const row = el("div", "radio-row");
  for (const opt of options) {
    const b = el("button", "radio-btn");
    b.innerHTML = opt.html;
    b.title = opt.title;
    if (opt.value === current) b.classList.add("on");
    b.addEventListener("click", () => {
      row.querySelectorAll(".radio-btn.on").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
      onChange(opt.value);
    });
    row.append(b);
  }
  wrap.append(name, row);
  parent.append(wrap);
}

const wave = (v: OscillatorType | LfoShape): RadioOption => ({ value: v, html: waveIcon(v), title: v });
const text = (v: string, label: string): RadioOption => ({ value: v, html: label, title: v });

const OSC_TYPES: readonly RadioOption[] = ["sine", "square", "sawtooth", "triangle", "pulse", "noise"].map((v) =>
  wave(v as OscillatorType),
);
const FILTER_TYPES: readonly RadioOption[] = [
  text("lowpass", "LP"),
  text("highpass", "HP"),
  text("bandpass", "BP"),
  text("notch", "NO"),
];
const LFO_TARGETS: readonly RadioOption[] = [
  text("off", "OFF"),
  text("pitch", "PIT"),
  text("amplitude", "AMP"),
  text("filter", "FLT"),
  text("pulseWidth", "PW"),
];
const LFO_SHAPES: readonly RadioOption[] = ["sine", "square", "sawtooth", "triangle"].map((v) => wave(v as LfoShape));

export interface MorphModel {
  /** Display name of the A sound, or null when the slot is empty. */
  a: string | null;
  b: string | null;
  t: number;
  /** Collapsed to a single chip when false. */
  open: boolean;
}
export interface MorphRack {
  /** The A / B drop targets, so the caller can register palette drop zones.
   *  Null while collapsed — there are no slots on screen to drop onto. */
  slotA: HTMLElement | null;
  slotB: HTMLElement | null;
}

// The Morph rack: blend two sounds into a third, real one. Always visible (it
// used to appear only after the drag-tile-onto-tile handshake, which nobody
// found) and it names both ends explicitly, so you can see what you're between.
//
// B sits above A because the fader reads bottom-up: down = pure A, up = pure B.
// Lives in its own container, so the debounced main-rack rebuild can never kill
// a live drag on the Morph fader.
export function buildMorphRack(
  container: HTMLElement,
  model: MorphModel,
  ref: ParamRef,
  handlers: {
    onPick: (which: "a" | "b") => void;
    onToggle: () => void;
    onClear: () => void;
    onSelect?: (r: ParamRef) => void;
  },
): MorphRack {
  container.innerHTML = "";
  container.style.display = "";
  const armed = model.a !== null && model.b !== null;

  // Collapsed: one chip, so an unused Morph costs a sliver of the rack instead
  // of a permanently greyed-out stage pretending to be broken.
  if (!model.open) {
    const chip = el("button", "morph-collapsed");
    chip.setAttribute("type", "button");
    chip.title = "Morph — blend two sounds into a new one. Click to open.";
    chip.innerHTML = `<span class="morph-collapsed-label">Morph</span><span class="morph-collapsed-plus">+</span>`;
    chip.addEventListener("click", handlers.onToggle);
    container.append(chip);
    return { slotA: null, slotB: null };
  }

  const s = stage(container, "Morph", "is-morph" + (armed ? "" : " is-idle"));
  // ✕ empties both slots and collapses — the way back out.
  const close = el("button", "morph-close");
  close.setAttribute("type", "button");
  close.title = "Clear both sounds and close Morph";
  close.textContent = "✕";
  close.addEventListener("click", handlers.onClear);
  s.append(close);

  const slot = (which: "a" | "b", name: string | null): HTMLElement => {
    const el0 = el("button", `morph-slot morph-${which}` + (name ? " filled" : ""));
    el0.setAttribute("type", "button");
    el0.title = name
      ? `${which.toUpperCase()}: ${name} — click to replace with the current sound, or drop a sample tile here`
      : `Set ${which.toUpperCase()} from the current sound, or drop a sample tile here`;
    const tag = el("span", "morph-tag");
    tag.textContent = which.toUpperCase();
    const val = el("span", "morph-name");
    val.textContent = name ?? "pick a sound";
    el0.append(tag, val);
    el0.addEventListener("click", () => handlers.onPick(which));
    return el0;
  };

  const slotB = slot("b", model.b);
  s.append(slotB);
  const f = faderRow(s);
  vfader(
    container,
    f,
    { label: "A↔B", min: 0, max: 1, step: 0.01, value: model.t, fmt: ref.fmt },
    (v) => ref.set(v),
    handlers.onSelect,
  );
  const slotA = slot("a", model.a);
  s.append(slotA);

  if (!armed) {
    const note = el("p", "morph-note");
    note.textContent = "Fill both slots to blend.";
    s.append(note);
  }
  return { slotA, slotB };
}

// Build the compact synth rack bound to `patch`. `onChange` runs after any edit.
// `onSelect` (optional) fires when a fader's label/value is tapped, handing its
// ParamRef to the right-edge context slider for fine tuning.
export function buildControls(
  container: HTMLElement,
  patch: Patch,
  onChange: () => void,
  onSelect?: (ref: ParamRef) => void,
): void {
  container.innerHTML = "";

  // Lazily attach the optional blocks so their faders always have a home.
  // amount 0 / drive 0 / fm depth 0 are sonically inert, so this stays additive.
  const pe = patch.pitchEnv ?? (patch.pitchEnv = { amount: 0, decay: 0.08 });
  const fm = patch.fm ?? (patch.fm = { ratio: 1, depth: 0 });
  const fe = patch.filterEnv ?? (patch.filterEnv = { amount: 0, decay: 0.15 });

  // --- Oscillator ---
  const osc = stage(container, "Osc");
  iconRadio(osc, "Wave", OSC_TYPES, patch.oscillator.type, (v) => {
    patch.oscillator.type = v as OscillatorType;
    onChange();
  });
  const oscF = faderRow(osc);
  vfader(container, oscF, { label: "Detune", min: -1200, max: 1200, step: 1, value: patch.oscillator.detune, fmt: cents }, (v) => {
    patch.oscillator.detune = v;
    onChange();
  }, onSelect);
  vfader(container, oscF, { label: "Width", min: 0.05, max: 0.95, step: 0.01, value: patch.oscillator.pulseWidth, fmt: ratio }, (v) => {
    patch.oscillator.pulseWidth = v;
    onChange();
  }, onSelect);
  // Pitch envelope — fast downward sweep at note-on (kick / tom / zap).
  vfader(container, oscF, { label: "P.Env", min: 0, max: 48, step: 1, value: pe.amount, fmt: (v) => `+${Math.round(v)}` }, (v) => {
    pe.amount = v;
    onChange();
  }, onSelect);
  vfader(container, oscF, { label: "P.Dec", min: 0.01, max: 0.6, step: 0.01, value: pe.decay, fmt: sec }, (v) => {
    pe.decay = v;
    onChange();
  }, onSelect);

  // --- Filter ---
  const filter = stage(container, "Filter");
  iconRadio(filter, "Type", FILTER_TYPES, patch.filter.type, (v) => {
    patch.filter.type = v as FilterType;
    onChange();
  });
  const filterF = faderRow(filter);
  vfader(container, filterF, { label: "Cutoff", min: 40, max: 18000, step: 1, value: patch.filter.cutoff, fmt: hz, scale: "log" }, (v) => {
    patch.filter.cutoff = v;
    onChange();
  }, onSelect);
  vfader(container, filterF, { label: "Reso", min: 0.1, max: 24, step: 0.1, value: patch.filter.q, fmt: ratio }, (v) => {
    patch.filter.q = v;
    onChange();
  }, onSelect);
  // Drive — soft-clip saturation after the filter (grit / metal / punch).
  vfader(container, filterF, { label: "Drive", min: 0, max: 1, step: 0.01, value: patch.drive ?? 0, fmt: ratio }, (v) => {
    patch.drive = v;
    onChange();
  }, onSelect);

  // --- Amp envelope --- (right after Filter: the core osc→filter→amp path
  // reads first; the dashed modulator stages follow.)
  const env = stage(container, "Amp");
  const envF = faderRow(env);
  vfader(container, envF, { label: "Atk", min: 0, max: 2, step: 0.01, value: patch.ampEnv.attack, fmt: sec }, (v) => {
    patch.ampEnv.attack = v;
    onChange();
  }, onSelect);
  vfader(container, envF, { label: "Dec", min: 0, max: 2, step: 0.01, value: patch.ampEnv.decay, fmt: sec }, (v) => {
    patch.ampEnv.decay = v;
    onChange();
  }, onSelect);
  vfader(container, envF, { label: "Sus", min: 0, max: 1, step: 0.01, value: patch.ampEnv.sustain, fmt: ratio }, (v) => {
    patch.ampEnv.sustain = v;
    onChange();
  }, onSelect);
  vfader(container, envF, { label: "Rel", min: 0, max: 3, step: 0.01, value: patch.ampEnv.release, fmt: sec }, (v) => {
    patch.ampEnv.release = v;
    onChange();
  }, onSelect);

  // --- FM operator (modulator → bell / clang / metal) ---
  const fmStage = stage(container, "FM", "is-mod");
  const fmF = faderRow(fmStage);
  vfader(container, fmF, { label: "Ratio", min: 0.1, max: 8, step: 0.05, value: fm.ratio, fmt: ratio }, (v) => {
    fm.ratio = v;
    onChange();
  }, onSelect);
  vfader(container, fmF, { label: "Depth", min: 0, max: 12, step: 0.1, value: fm.depth, fmt: ratio }, (v) => {
    fm.depth = v;
    onChange();
  }, onSelect);

  // --- Filter envelope (cutoff sweep at note-on → plucks / zaps / wah) ---
  const fEnv = stage(container, "F.Env", "is-mod");
  const feF = faderRow(fEnv);
  vfader(container, feF, { label: "Amount", min: 0, max: 10000, step: 50, value: fe.amount, fmt: hz }, (v) => {
    fe.amount = v;
    onChange();
  }, onSelect);
  vfader(container, feF, { label: "Decay", min: 0.01, max: 1.5, step: 0.01, value: fe.decay, fmt: sec }, (v) => {
    fe.decay = v;
    onChange();
  }, onSelect);

  // --- LFO (modulator) ---
  const lfo = stage(container, "LFO", "is-mod");
  iconRadio(lfo, "Target", LFO_TARGETS, patch.lfo.target, (v) => {
    patch.lfo.target = v as LfoTarget;
    onChange();
  });
  iconRadio(lfo, "Shape", LFO_SHAPES, patch.lfo.shape, (v) => {
    patch.lfo.shape = v as LfoShape;
    onChange();
  });
  const lfoF = faderRow(lfo);
  vfader(container, lfoF, { label: "Rate", min: 0.1, max: 20, step: 0.1, value: patch.lfo.rate, fmt: hz, scale: "log" }, (v) => {
    patch.lfo.rate = v;
    onChange();
  }, onSelect);
  vfader(container, lfoF, { label: "Depth", min: 0, max: 100, step: 0.5, value: patch.lfo.depth, fmt: ratio }, (v) => {
    patch.lfo.depth = v;
    onChange();
  }, onSelect);
}
